import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// [CU-869eyxr3j] Időpontválasztó a jelentkezési űrlapon — a projekt karrieroldali
// eseményének (career_booking='Y') szabad időpontjaiból választ a jelölt, a
// booking miniapp nap → időpont mintájára. Az érték a szülő formData-ban EGY
// JSON string: {"date_id":<int|null>,"date_from":"Y-m-d H:i:s","date_till":"..."}
// — a szerver (Jobs::apply) ezt ellenőrzi a friss szabad-lista ellen és foglal.
import React, { useMemo } from 'react';
const FONT = 'Rubik, -apple-system, Roboto, Helvetica, sans-serif';
/** a tárolt JSON-érték visszafejtése (hibás értéknél null) */
export function parseBookingChoice(value) {
    if (!value)
        return null;
    try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed.date_from === 'string' && typeof parsed.date_till === 'string') {
            return {
                date_id: parsed.date_id ? Number(parsed.date_id) : null,
                date_from: parsed.date_from,
                date_till: parsed.date_till,
            };
        }
    }
    catch (e) {
        return null;
    }
    return null;
}
/** 'YYYY-MM-DD' → lokalizált, hétköznap-neves dátum (a szerver Europe/Budapest fali-idejét mutatjuk) */
function formatDayLabel(dateKey, lang) {
    const parts = dateKey.split('-');
    if (parts.length !== 3)
        return dateKey;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    try {
        return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'hu-HU', { month: 'short', day: 'numeric', weekday: 'short' });
    }
    catch (e) {
        return dateKey;
    }
}
export function BookingSlotPicker({ descriptor, value, onChange, translations, colors, hasError, lang = 'hu', paramName }) {
    const days = useMemo(() => Object.keys(descriptor.slots || {}).sort(), [descriptor]);
    const choice = useMemo(() => parseBookingChoice(value), [value]);
    // a kiválasztott nap: a választott slot napja, különben az első nap
    const selectedDay = useMemo(() => {
        if (choice) {
            const day = choice.date_from.slice(0, 10);
            if (days.indexOf(day) >= 0)
                return day;
        }
        return days.length > 0 ? days[0] : '';
    }, [choice, days]);
    const [activeDay, setActiveDay] = React.useState(selectedDay);
    React.useEffect(() => {
        if (days.indexOf(activeDay) < 0) {
            setActiveDay(selectedDay);
        }
    }, [days, selectedDay, activeDay]);
    const isSlotSelected = (slot) => {
        if (!choice)
            return false;
        if (slot.date_id && choice.date_id)
            return Number(slot.date_id) === Number(choice.date_id);
        return slot.date_from === choice.date_from && slot.date_till === choice.date_till;
    };
    const selectSlot = (slot) => {
        onChange(JSON.stringify({
            date_id: slot.date_id ? Number(slot.date_id) : null,
            date_from: slot.date_from,
            date_till: slot.date_till,
        }));
    };
    const chipStyle = (active) => ({
        fontFamily: FONT,
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        padding: '8px 14px',
        borderRadius: colors.inputRadius || '8px',
        border: `1px solid ${active ? colors.primary : colors.inputBorder}`,
        backgroundColor: active ? colors.primary : '#ffffff',
        color: active ? '#ffffff' : colors.text,
        cursor: 'pointer',
        transition: 'all 0.15s',
    });
    if (days.length === 0) {
        return (_jsx("div", { "data-testid": "booking-picker-empty", style: { fontFamily: FONT, color: colors.textMuted, fontSize: '0.875rem' }, children: translations.noSlots }));
    }
    const daySlots = descriptor.slots[activeDay] || [];
    return (_jsxs("div", { "data-testid": "booking-picker", "data-param": paramName, style: {
            border: `1px solid ${hasError ? colors.inputBorderError : colors.inputBorder}`,
            borderRadius: colors.inputRadius || '8px',
            padding: '16px',
            backgroundColor: '#ffffff',
        }, children: [_jsx("div", { style: { fontFamily: FONT, color: colors.text, fontSize: '1rem', fontWeight: 500, marginBottom: '4px' }, children: descriptor.event_name }), _jsxs("div", { style: { fontFamily: FONT, color: colors.textMuted, fontSize: '0.8125rem', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px' }, children: [descriptor.interview_length > 0 && (_jsxs("span", { children: [translations.duration, ": ", descriptor.interview_length, " ", translations.minutes] })), descriptor.online ? (_jsxs("span", { children: [translations.location, ": ", translations.online] })) : descriptor.location ? (_jsxs("span", { children: [translations.location, ": ", descriptor.location] })) : null] }), _jsx("div", { style: { fontFamily: FONT, color: colors.text, fontSize: '0.875rem', marginBottom: '8px' }, children: translations.selectDay }), _jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }, "data-testid": "booking-picker-days", children: days.map((day) => (_jsx("button", { type: "button", "data-testid": "booking-picker-day", "data-day": day, "aria-pressed": day === activeDay, style: chipStyle(day === activeDay), onClick: () => setActiveDay(day), children: formatDayLabel(day, lang) }, day))) }), _jsx("div", { style: { fontFamily: FONT, color: colors.text, fontSize: '0.875rem', marginBottom: '8px' }, children: translations.selectTime }), _jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '8px' }, "data-testid": "booking-picker-times", children: daySlots.map((slot) => {
                    const active = isSlotSelected(slot);
                    return (_jsx("button", { type: "button", "data-testid": "booking-picker-slot", "data-date-from": slot.date_from, "aria-pressed": active, style: chipStyle(active), onClick: () => selectSlot(slot), children: slot.time }, `${slot.date_from}-${slot.date_id || ''}`));
                }) }), choice && (_jsxs("div", { "data-testid": "booking-picker-selected", style: { fontFamily: FONT, color: colors.text, fontSize: '0.875rem', marginTop: '12px' }, children: [translations.selected, ": ", _jsxs("strong", { children: [choice.date_from.slice(0, 16), " - ", choice.date_till.slice(11, 16)] })] }))] }));
}
